import { ApplicationConfig, provideAppInitializer, inject } from '@angular/core';
import { provideRouter, withRouterConfig, TitleStrategy } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';

import { AppTitleStrategy } from './core/app-title.strategy';

import {
  provideKeycloak,
  includeBearerTokenInterceptor,
  createInterceptorCondition,
  IncludeBearerTokenCondition,
  INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
  withAutoRefreshToken,
  AutoRefreshTokenService,
  UserActivityService,
} from 'keycloak-angular';

import { routes } from './app.routes';
import { environment } from '../environments/environment';
import { NotificationHubService } from './core/services/notification-hub.service';

/**
 * keycloak-angular v21 -- functional provider replaces the old class-based
 * KeycloakService bootstrap. provideKeycloak() registers Keycloak as an
 * injectable instance (inject(Keycloak)) and runs the init() chain at app
 * startup with onLoad='check-sso' (non-blocking; the SPA renders the landing
 * page even when no session exists).
 */

/**
 * Bearer interceptor condition -- match any HTTP call going through HttpClient
 * whose URL contains '/api/' or hits the /me endpoint. Excludes static asset
 * fetches (/assets/*) so the runtime config.json doesn't get a redundant
 * Authorization header.
 */
const bearerCondition = createInterceptorCondition<IncludeBearerTokenCondition>({
  urlPattern: /^(https?:\/\/[^/]+)?\/(api\/|me$|me\?)/i,
});

/**
 * Factory rather than a top-level const. Why: provideKeycloak() captures
 * environment.keycloak.{url,realm,clientId} as plain object literal values at
 * the time the providers array is evaluated. If appConfig were a const, those
 * values would be captured at MODULE LOAD time -- BEFORE main.ts's
 * loadRuntimeConfig() has populated them from /assets/config.json. Result: the
 * redirect to Keycloak ships with `client_id=` empty and the flow loops back.
 *
 * main.ts awaits loadRuntimeConfig() THEN invokes buildAppConfig(), so the
 * environment object has its production values populated before this factory
 * reads them.
 */
export function buildAppConfig(): ApplicationConfig {
  return {
    providers: [
      provideKeycloak({
        config: {
          url: environment.keycloak.url,
          realm: environment.keycloak.realm,
          clientId: environment.keycloak.clientId,
        },
        initOptions: {
          // 'login-required' auto-redirects unauthenticated visitors straight to
          // Keycloak instead of rendering the SPA's landing-page Sign In button.
          // After login Keycloak redirects back to the SPA with the session cookie
          // + access token. Use 'check-sso' if you want an explicit Sign In step.
          onLoad: 'login-required',
          pkceMethod: 'S256',
          checkLoginIframe: false,
          silentCheckSsoRedirectUri: window.location.origin + '/assets/silent-check-sso.html',
          // keycloak-js's 3p-cookies probe runs before the silent-check-sso fallback.
          // Default messageReceiveTimeout is 10s which produces a noisy console error +
          // 10s stall. Short-circuit to 2s.
          messageReceiveTimeout: 2000,
        },
        // 2026-06-11 incident: includeBearerTokenInterceptor attaches kc.token but
        // NEVER refreshes it, and nothing else in the HTTP path called updateToken()
        // (only the two SignalR hubs did, on (re)connect). ~5 min after sign-in the
        // access token expired and every API call 401'd — surfaced as the upload
        // page's generic error. withAutoRefreshToken refreshes on expiry as long as
        // the user is active; after 30 min of inactivity it logs out (FERPA-friendly
        // for shared school-office machines) rather than refreshing forever.
        features: [
          withAutoRefreshToken({
            onInactivityTimeout: 'logout',
            sessionTimeout: 1800000,
          }),
        ],
        providers: [
          AutoRefreshTokenService,
          UserActivityService,
          { provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG, useValue: [bearerCondition] },
        ],
      }),

      // paramsInheritanceStrategy: 'always' — non-empty child routes (e.g. the
      // 'result' child under 'submissions/:id') inherit the parent's :id param.
      // Without this, ValidationResultComponent's route.snapshot.paramMap.get('id')
      // returns '' under the default 'emptyOnly' strategy.
      provideRouter(routes, withRouterConfig({ paramsInheritanceStrategy: 'always' })),
      // Custom TitleStrategy: every navigation sets document.title (titled →
      // "X · NEPF Upload Portal", titleless → bare default) so the browser tab
      // never sticks on the previous page's title.
      { provide: TitleStrategy, useClass: AppTitleStrategy },
      provideHttpClient(withInterceptors([includeBearerTokenInterceptor])),
      provideAnimations(),

      // Plan 03-12 -- bootstrap the global SignalR notifications hub AFTER Keycloak
      // finishes init. Failures during initial connect are caught + logged so a hub
      // outage does not block app startup (the user can still upload + view
      // submissions; they just won't see real-time push toasts until reconnect).
      provideAppInitializer(() => {
        const hub = inject(NotificationHubService);
        return hub.connect().catch((err: unknown) => {
          // Non-fatal: log and let the user proceed without real-time notifications.
          // eslint-disable-next-line no-console
          console.error('NotificationHubService initial connect failed', err);
        });
      }),
    ],
  };
}

/**
 * Legacy export kept for any test or external consumer that imports `appConfig`
 * directly. Not used by main.ts (which calls buildAppConfig() after runtime
 * config loads). Reads whatever environment values are populated at the call
 * site -- in test setups using TestBed this is generally fine; in production
 * bootstrap, prefer buildAppConfig().
 */
export const appConfig: ApplicationConfig = buildAppConfig();
